import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { civilLawContracts, employees, departmentsApi } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

interface Form {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  contract_subject: string;
  date_from: string;
  date_to: string;
  total_amount: string;
  calc_type: 'ONCE' | 'DAILY' | 'HOURLY';
  department_id: string;
  comment: string;
}

const today = new Date();
const EMPTY: Form = {
  organization_id: '',
  document_number: '',
  document_date: today.toISOString().slice(0, 16),
  employee_id: '',
  contract_subject: '',
  date_from: today.toISOString().slice(0, 10),
  date_to: '',
  total_amount: '0',
  calc_type: 'ONCE',
  department_id: '',
  comment: '',
};

export default function CivilLawContractEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [search] = useSearchParams();
  const isNew = !id || id === 'new';
  const empFromUrl = search.get('employee');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, hasPermissionInAnyOrg, loading: permsLoading } =
    usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['civil-law-contract', id],
    queryFn: () => civilLawContracts.get(id!),
    enabled: !isNew,
  });
  const [form, setForm] = useState<Form>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  // Лише працівники виду CIVIL_LAW (договір ЦПХ — для ГПХ).
  const empListQ = useQuery({
    queryKey: ['employees-civil', form.organization_id],
    queryFn: () => employees.list({ organization_id: form.organization_id, limit: 1000 }),
    enabled: !!form.organization_id,
  });
  const civilEmps = (empListQ.data?.items ?? []).filter(
    (e) => e.employment_type === 'CIVIL_LAW',
  );
  // Підрозділи обраної юрособи (для поля «Підрозділ»).
  const depQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
        employee_id: empFromUrl ?? f.employee_id,
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        employee_id: d.employee_id,
        contract_subject: d.contract_subject ?? '',
        date_from: d.date_from,
        date_to: d.date_to ?? '',
        total_amount: String(d.total_amount),
        calc_type: (d.calc_type as 'ONCE' | 'DAILY' | 'HOURLY') ?? 'ONCE',
        department_id: d.department_id ?? '',
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data, availableOrgs, empFromUrl]);

  const state = docQuery.data?.state ?? 'DRAFT';
  const isPosted = !isNew && state === 'POSTED';
  const isCancelled = !isNew && state === 'CANCELLED';

  const create = useMutation({
    mutationFn: () => civilLawContracts.create({
      organization_id: form.organization_id,
      document_number: form.document_number || undefined,
      document_date: new Date(form.document_date).toISOString(),
      employee_id: form.employee_id,
      contract_subject: form.contract_subject || null,
      date_from: form.date_from,
      date_to: form.date_to || null,
      total_amount: form.total_amount,
      calc_type: form.calc_type,
      department_id: form.department_id || null,
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['civil-law-contracts'] });
      navigate(`/civil-law-contracts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const update = useMutation({
    mutationFn: () => civilLawContracts.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contract_subject: form.contract_subject || null,
      date_from: form.date_from,
      date_to: form.date_to || null,
      total_amount: form.total_amount,
      calc_type: form.calc_type,
      department_id: form.department_id || null,
      comment: form.comment || null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['civil-law-contract', id] }),
    onError: (e) => setError(extractError(e)),
  });

  // Новий: зберегти та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await civilLawContracts.create({
        organization_id: form.organization_id,
        document_number: form.document_number || undefined,
        document_date: new Date(form.document_date).toISOString(),
        employee_id: form.employee_id,
        contract_subject: form.contract_subject || null,
        date_from: form.date_from,
        date_to: form.date_to || null,
        total_amount: form.total_amount,
      calc_type: form.calc_type,
        department_id: form.department_id || null,
        comment: form.comment || null,
      });
      await civilLawContracts.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['civil-law-contracts'] });
      navigate(`/civil-law-contracts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: спершу зберегти правки, потім провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await civilLawContracts.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        contract_subject: form.contract_subject || null,
        date_from: form.date_from,
        date_to: form.date_to || null,
        total_amount: form.total_amount,
      calc_type: form.calc_type,
        department_id: form.department_id || null,
        comment: form.comment || null,
      });
      await civilLawContracts.post(id!);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['civil-law-contract', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => civilLawContracts.unpost(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['civil-law-contract', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => civilLawContracts.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['civil-law-contracts'] });
      navigate('/employees');
    },
  });

  function validate(): boolean {
    if (!form.organization_id) { setError('Оберіть організацію'); return false; }
    if (!form.employee_id) { setError('Оберіть працівника (вид ГПХ)'); return false; }
    if (!form.date_from) { setError('Вкажіть дату початку'); return false; }
    setError(null);
    return true;
  }
  function onSave() {
    if (!validate()) return;
    isNew ? create.mutate() : update.mutate();
  }
  function onSaveAndPost() {
    if (!validate()) return;
    createAndPost.mutate();
  }
  function onUpdateAndPost() {
    if (!validate()) return;
    updateAndPost.mutate();
  }

  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{
          padding: '1rem', background: '#fef3c7', borderRadius: 6,
          border: '1px solid #fbbf24', color: '#92400e',
          maxWidth: 600, margin: '1rem auto', textAlign: 'center',
        }}>
          ⚠ У вас немає прав створювати договори ЦПХ у жодній організації.
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий договір ЦПХ' : docQuery.data?.document_number ?? '...'}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
          {isCancelled && (
            <span style={{ marginLeft: '0.75rem', color: '#dc2626', fontSize: '1rem' }}>
              ✗ скасовано
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/employees')}>До списку</button>
          {!isCancelled && (
            <button className="primary" onClick={onSave}
                    disabled={isPosted || create.isPending || update.isPending}>
              Зберегти
            </button>
          )}
          {isNew && (
            <button className="primary" onClick={onSaveAndPost}
                    disabled={create.isPending || createAndPost.isPending}
                    title="Зберігає та проводить документ">
              ▶ Провести
            </button>
          )}
          {!isNew && !isPosted && !isCancelled && (
            <button className="primary" onClick={onUpdateAndPost}
                    disabled={updateAndPost.isPending || update.isPending}
                    title="Зберігає поточні зміни та проводить документ">▶ Провести</button>
          )}
          {isPosted && (
            <button onClick={() => unpost.mutate()} disabled={unpost.isPending}>
              ⏏ Розпровести
            </button>
          )}
          {!isNew && !isPosted && !isCancelled && (
            <button className="danger"
                    onClick={() => confirm('Видалити договір ЦПХ?').then((ok) => {
                      if (ok) remove.mutate();
                    })}>
              Видалити
            </button>
          )}
        </div>
      </div>

      {isCancelled && (
        <div style={{
          background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b',
          borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.75rem',
        }}>
          🚫 Документ скасовано. {docQuery.data?.cancellation_reason}
        </div>
      )}
      {error && (
        <div style={{
          background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b',
          borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.75rem',
        }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew || isPosted}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
            <option value="">—</option>
            {(orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Номер</label>
          <input value={form.document_number} placeholder="(авто)"
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })} />
        </div>
        <div className="field">
          <label>Дата документа</label>
          <DatePopover withTime value={form.document_date} disabled={isPosted}
                 onChange={(v) => setForm({ ...form, document_date: v })} />
        </div>
        <div className="field">
          <label>Працівник (ГПХ) *</label>
          <select value={form.employee_id} disabled={!isNew || isPosted}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
            <option value="">—</option>
            {civilEmps.map((e) => (
              <option key={e.id} value={e.id}>{e.full_name ?? e.id}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Дата початку *</label>
          <DatePopover value={form.date_from} disabled={isPosted}
                 onChange={(v) => setForm({ ...form, date_from: v })} />
        </div>
        <div className="field">
          <label>Дата завершення</label>
          <DatePopover value={form.date_to} disabled={isPosted}
                 onChange={(v) => setForm({ ...form, date_to: v })} />
        </div>
        <div className="field">
          <label>Вид розрахунку</label>
          <select value={form.calc_type} disabled={isPosted}
                  onChange={(e) => setForm({ ...form, calc_type: e.target.value as 'ONCE' | 'DAILY' | 'HOURLY' })}>
            <option value="ONCE">разово</option>
            <option value="DAILY">подобово</option>
            <option value="HOURLY">погодинно</option>
          </select>
        </div>
        <div className="field">
          <label>Розмір, грн{form.calc_type === 'DAILY' ? ' (за день)'
            : form.calc_type === 'HOURLY' ? ' (за годину)' : ''}</label>
          <input type="number" step="0.01" value={form.total_amount} disabled={isPosted}
                 onChange={(e) => setForm({ ...form, total_amount: e.target.value })} />
        </div>
        <div className="field">
          <label>Підрозділ</label>
          <select value={form.department_id} disabled={isPosted}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
            <option value="">— не вказано —</option>
            {(depQ.data?.items ?? []).map((d) => (
              <option key={d.id} value={d.id}>{d.code} — {d.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Предмет договору</label>
          <input value={form.contract_subject} disabled={isPosted}
                 onChange={(e) => setForm({ ...form, contract_subject: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Коментар</label>
          <input value={form.comment} disabled={isPosted}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} />
        </div>
      </div>
    </div>
  );
}
